import { useA11y } from '@dsx-react/provider';
import { useControllableState, classNames, Dialog } from '@dsx-react/util';
import { useFloating, autoUpdate, offset, flip, size } from '@floating-ui/react';
import { forwardRef, createContext, useRef, useState, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Text } from '@dsx-react/text';
import { UNSAFE_Icon, MdCheck } from '@dsx-react/icon';
import { Link } from '@dsx-react/link';

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;
  if (!css || typeof document === 'undefined') {
    return;
  }
  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';
  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$2 = ".GridMenu-module_list__KEcAb {\n  display: grid;\n  grid-template-columns: auto 1fr auto auto;\n  column-gap: var(--size-100);\n}\n\n.GridMenu-module_item__KTuTc,\n.GridMenu-module_group-header__cJqz3 {\n  display: grid;\n  grid-column: 1/5;\n  grid-template-columns: subgrid;\n  grid-template-areas: \"start primary   shortcut  end\" \".     secondary secondary .\";\n  align-items: baseline;\n  padding: var(--size-150);\n  border-radius: var(--border-radius-default);\n}\n\n.GridMenu-module_icon-start__-dy66 {\n  -ms-grid-row: 1;\n  -ms-grid-column: 1;\n  grid-area: start;\n  display: flex;\n  flex-direction: row;\n  gap: var(--size-100);\n  justify-content: flex-end;\n}\n\n.GridMenu-module_primary__i2bsj {\n  -ms-grid-row: 1;\n  -ms-grid-column: 2;\n  grid-area: primary;\n}\n\n.GridMenu-module_secondary__PQxxn {\n  -ms-grid-row: 2;\n  -ms-grid-column: 2;\n  -ms-grid-column-span: 2;\n  grid-area: secondary;\n}\n\n.GridMenu-module_shortcut__9qas5 {\n  -ms-grid-row: 1;\n  -ms-grid-column: 3;\n  grid-area: shortcut;\n  text-align: right;\n}\n\n.GridMenu-module_icon-end__PJvf2 {\n  -ms-grid-row: 1;\n  -ms-grid-column: 4;\n  grid-area: end;\n  text-align: center;\n}\n\n.GridMenu-module_item__KTuTc.GridMenu-module_disabled__dGn1U {\n  opacity: 0.4;\n}\n\n.GridMenu-module_item__KTuTc:not(.GridMenu-module_disabled__dGn1U) {\n  cursor: pointer;\n}\n\n.GridMenu-module_item__KTuTc:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_focus__xctHG {\n  outline: 2px solid var(--color-primary);\n  z-index: 1;\n}\n\n.GridMenu-module_item__KTuTc:not(.GridMenu-module_disabled__dGn1U):hover {\n  background-color: var(--color-gray-75);\n}\n\n.GridMenu-module_item__KTuTc:not(.GridMenu-module_disabled__dGn1U):active,\n.GridMenu-module_item__KTuTc:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_active__tsWeJ {\n  background-color: var(--color-gray-100);\n}\n\na.GridMenu-module_item__KTuTc,\na.GridMenu-module_item__KTuTc:hover,\na.GridMenu-module_item__KTuTc:visited {\n  color: inherit;\n  -webkit-text-decoration: none;\n  text-decoration: none;\n}\n\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb {\n  color: var(--color-gray-800);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb .GridMenu-module_secondary__PQxxn {\n  color: var(--color-gray-700);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb .GridMenu-module_shortcut__9qas5 {\n  color: var(--color-gray-500);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):hover {\n  color: var(--color-primary-600);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):hover .GridMenu-module_secondary__PQxxn,\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):hover .GridMenu-module_shortcut__9qas5 {\n  color: inherit;\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):active, .GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_active__tsWeJ {\n  color: var(--color-primary-700);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):active .GridMenu-module_secondary__PQxxn,\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U):active .GridMenu-module_shortcut__9qas5, .GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_active__tsWeJ .GridMenu-module_secondary__PQxxn,\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-default__6qqsb:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_active__tsWeJ .GridMenu-module_shortcut__9qas5 {\n  color: inherit;\n}\n\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-danger__1ajw- {\n  color: var(--color-danger-500);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-danger__1ajw-:not(.GridMenu-module_disabled__dGn1U):hover {\n  color: var(--color-danger-600);\n}\n.GridMenu-module_item__KTuTc.GridMenu-module_variant-danger__1ajw-:not(.GridMenu-module_disabled__dGn1U):active, .GridMenu-module_item__KTuTc.GridMenu-module_variant-danger__1ajw-:not(.GridMenu-module_disabled__dGn1U).GridMenu-module_active__tsWeJ {\n  color: var(--color-danger-700);\n}\n\n.GridMenu-module_group-header__cJqz3 {\n  background-color: var(--color-gray-0);\n  color: var(--color-gray-600);\n}\n\n.GridMenu-module_group-header__cJqz3.GridMenu-module_size-default__ikyWM {\n  font-size: 0.875rem;\n  font-weight: 600;\n  line-height: 1.25rem;\n  letter-spacing: var(--letter-spacing-14);\n}\n\n.GridMenu-module_group-header__cJqz3.GridMenu-module_size-small__lyYnJ {\n  font-size: 0.75rem;\n  font-weight: 600;\n  line-height: 1rem;\n  letter-spacing: var(--letter-spacing-12);\n}\n\n.GridMenu-module_group-header-text__frI5- {\n  grid-column: 1/5;\n}\n\n.GridMenu-module_item__KTuTc.GridMenu-module_size-default__ikyWM svg {\n  height: 1.5rem;\n  width: 1.5rem;\n  vertical-align: -0.375rem;\n}\n\n.GridMenu-module_item__KTuTc.GridMenu-module_size-small__lyYnJ svg {\n  height: 1rem;\n  width: 1rem;\n  vertical-align: -0.1875rem;\n}\n\n.GridMenu-module_separator__SHTtF {\n  grid-column: 1/5;\n  margin: var(--size-100) var(--size-150);\n  border-bottom-color: var(--color-gray-100);\n  border-bottom-style: solid;\n  border-bottom-width: 1px;\n}";
var styles$2 = {"list":"GridMenu-module_list__KEcAb","item":"GridMenu-module_item__KTuTc","group-header":"GridMenu-module_group-header__cJqz3","icon-start":"GridMenu-module_icon-start__-dy66","primary":"GridMenu-module_primary__i2bsj","secondary":"GridMenu-module_secondary__PQxxn","shortcut":"GridMenu-module_shortcut__9qas5","icon-end":"GridMenu-module_icon-end__PJvf2","disabled":"GridMenu-module_disabled__dGn1U","focus":"GridMenu-module_focus__xctHG","active":"GridMenu-module_active__tsWeJ","variant-default":"GridMenu-module_variant-default__6qqsb","variant-danger":"GridMenu-module_variant-danger__1ajw-","size-default":"GridMenu-module_size-default__ikyWM","size-small":"GridMenu-module_size-small__lyYnJ","group-header-text":"GridMenu-module_group-header-text__frI5-","separator":"GridMenu-module_separator__SHTtF"};
styleInject(css_248z$2);

const GridMenuList = /*#__PURE__*/forwardRef(function GridMenuList(props, ref) {
  const {
    children
  } = props;
  return /*#__PURE__*/jsx("div", {
    className: styles$2['list'],
    ref: ref,
    children: children
  });
});

function getActionFromKeyboardEvent(event, open) {
  const {
    key
  } = event;
  if (open) {
    switch (key) {
      case 'Escape':
        return 'close';
      case 'Enter':
      case ' ':
        return 'select';
      case 'ArrowUp':
        return 'previous';
      case 'ArrowDown':
        return 'next';
      case 'PageUp':
        return 'first';
      case 'PageDown':
        return 'last';
      default:
        return null;
    }
  } else {
    switch (key) {
      case 'Enter':
      case ' ':
      case 'ArrowUp':
      case 'ArrowDown':
      case 'PageUp':
      case 'PageDown':
        return 'open';
      default:
        return null;
    }
  }
}
function navigateItems(action, listElement, activeElement) {
  switch (action) {
    case 'previous':
      return navigatePrevious(listElement, activeElement);
    case 'next':
      return navigateNext(listElement, activeElement);
    case 'first':
      return navigateFirst(listElement);
    case 'last':
      return navigateLast(listElement);
  }
}
function navigatePrevious(listElement, activeElement) {
  const initialElement = (activeElement === null || activeElement === void 0 ? void 0 : activeElement.previousSibling) || null;
  let element = initialElement;
  const abortAfterSteps = 100;
  for (let i = 0; i < abortAfterSteps; ++i) {
    if (element == null) {
      element = listElement.lastChild;
    }
    if (element.dataset['dsx-MenuItem-Selectable'] === 'true') {
      return element;
    }
    element = element.previousSibling;
    if (element == initialElement) {
      return null;
    }
  }
  throw new Error('Could not find a valid menu item within a large number of steps.');
}
function navigateNext(listElement, activeElement) {
  const initialElement = (activeElement === null || activeElement === void 0 ? void 0 : activeElement.nextSibling) || null;
  let element = initialElement;
  const abortAfterSteps = 100;
  for (let i = 0; i < abortAfterSteps; ++i) {
    if (element == null) {
      element = listElement.firstChild;
    }
    if (element.dataset['dsx-MenuItem-Selectable'] === 'true') {
      return element;
    }
    element = element.nextSibling;
    if (element == initialElement) {
      return null;
    }
  }
  throw new Error('Could not find a valid menu item within a large number of steps.');
}
function navigateFirst(listElement) {
  return navigateNext(listElement, null);
}
function navigateLast(listElement) {
  return navigatePrevious(listElement, null);
}

var css_248z$1 = ".Menu-module_popover__EVUsy {\n  margin: 0;\n  padding: var(--size-100);\n  border: none;\n  background-color: var(--color-gray-0);\n  box-shadow: var(--UNSAFE-dsx-popover-box-shadow);\n  border-radius: var(--border-radius-default);\n  overflow-y: auto;\n}\n\n.Menu-module_popover__EVUsy.Menu-module_size-default__YbK85 {\n  min-width: 16rem;\n}\n\n.Menu-module_popover__EVUsy.Menu-module_size-small__tf2i- {\n  min-width: 12rem;\n}";
var styles$1 = {"popover":"Menu-module_popover__EVUsy","size-default":"Menu-module_size-default__YbK85","size-small":"Menu-module_size-small__tf2i-"};
styleInject(css_248z$1);

const MenuContext = /*#__PURE__*/createContext({});

var css_248z = ".TableMenu-module_list__rhibj {\n  display: table;\n  width: 100%;\n}\n\n.TableMenu-module_item__2DYRF,\n.TableMenu-module_group-header__BtvlS {\n  display: table-row;\n  border-radius: var(--border-radius-default);\n}\n\n.TableMenu-module_col__WoBci {\n  display: table-cell;\n  padding-top: 0.375rem;\n  padding-bottom: 0.375rem;\n}\n\n.TableMenu-module_col__WoBci:first-child {\n  padding-left: 0.25rem;\n}\n\n.TableMenu-module_col__WoBci:last-child {\n  padding-right: 0.25rem;\n}\n\n.TableMenu-module_col__WoBci > * {\n  padding-left: 0.125rem;\n  padding-right: 0.125rem;\n}\n\n.TableMenu-module_icon-start__jiEUu,\n.TableMenu-module_icon-end__u1Cw8 {\n  text-align: center;\n}\n\n.TableMenu-module_shortcut__3Juk6 {\n  text-align: right;\n}\n\n.TableMenu-module_item__2DYRF.TableMenu-module_disabled__h5okf {\n  opacity: 0.4;\n}\n\n.TableMenu-module_item__2DYRF:not(.TableMenu-module_disabled__h5okf) {\n  cursor: pointer;\n}\n\n.TableMenu-module_item__2DYRF:not(.TableMenu-module_disabled__h5okf).TableMenu-module_focus__McuhY {\n  outline: 2px solid var(--color-primary);\n  z-index: 1;\n}\n\n.TableMenu-module_item__2DYRF:not(.TableMenu-module_disabled__h5okf):hover {\n  background-color: var(--color-gray-75);\n}\n\n.TableMenu-module_item__2DYRF:not(.TableMenu-module_disabled__h5okf):active,\n.TableMenu-module_item__2DYRF:not(.TableMenu-module_disabled__h5okf).TableMenu-module_active__1X-uf {\n  background-color: var(--color-gray-100);\n}\n\na.TableMenu-module_item__2DYRF,\na.TableMenu-module_item__2DYRF:hover,\na.TableMenu-module_item__2DYRF:visited {\n  color: inherit;\n  -webkit-text-decoration: none;\n  text-decoration: none;\n}\n\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb {\n  color: var(--color-gray-800);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb .TableMenu-module_secondary__AfZLP {\n  color: var(--color-gray-700);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb .TableMenu-module_shortcut__3Juk6 {\n  color: var(--color-gray-500);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):hover {\n  color: var(--color-primary-600);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):hover .TableMenu-module_secondary__AfZLP,\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):hover .TableMenu-module_shortcut__3Juk6 {\n  color: inherit;\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):active, .TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf).TableMenu-module_active__1X-uf {\n  color: var(--color-primary-700);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):active .TableMenu-module_secondary__AfZLP,\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf):active .TableMenu-module_shortcut__3Juk6, .TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf).TableMenu-module_active__1X-uf .TableMenu-module_secondary__AfZLP,\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-default__KMuSb:not(.TableMenu-module_disabled__h5okf).TableMenu-module_active__1X-uf .TableMenu-module_shortcut__3Juk6 {\n  color: inherit;\n}\n\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-danger__mRlzi {\n  color: var(--color-danger-500);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-danger__mRlzi:not(.TableMenu-module_disabled__h5okf):hover {\n  color: var(--color-danger-600);\n}\n.TableMenu-module_item__2DYRF.TableMenu-module_variant-danger__mRlzi:not(.TableMenu-module_disabled__h5okf):active, .TableMenu-module_item__2DYRF.TableMenu-module_variant-danger__mRlzi:not(.TableMenu-module_disabled__h5okf).TableMenu-module_active__1X-uf {\n  color: var(--color-danger-700);\n}\n\n.TableMenu-module_group-header__BtvlS {\n  background-color: var(--color-gray-0);\n  color: var(--color-gray-600);\n}\n\n.TableMenu-module_group-header__BtvlS.TableMenu-module_size-default__5QLjo {\n  font-size: 0.875rem;\n  font-weight: 600;\n  line-height: 1.25rem;\n  letter-spacing: var(--letter-spacing-14);\n}\n\n.TableMenu-module_group-header__BtvlS.TableMenu-module_size-small__ZfJJQ {\n  font-size: 0.75rem;\n  font-weight: 600;\n  line-height: 1rem;\n  letter-spacing: var(--letter-spacing-12);\n}\n\n.TableMenu-module_item__2DYRF.TableMenu-module_size-default__5QLjo svg {\n  height: 1.5rem;\n  width: 1.5rem;\n  vertical-align: -0.375rem;\n}\n\n.TableMenu-module_item__2DYRF.TableMenu-module_size-small__ZfJJQ svg {\n  height: 1rem;\n  width: 1rem;\n  vertical-align: -0.1875rem;\n}\n\n.TableMenu-module_separator__TAKK6 {\n  display: table-row;\n}\n\n.TableMenu-module_separator__TAKK6 > .TableMenu-module_col__WoBci {\n  padding: 0.25rem 0;\n}\n\n.TableMenu-module_separator__TAKK6 > .TableMenu-module_col__WoBci::after {\n  content: \"\";\n  display: block;\n  border-bottom-color: var(--color-gray-100);\n  border-bottom-style: solid;\n  border-bottom-width: 1px;\n}";
var styles = {"list":"TableMenu-module_list__rhibj","item":"TableMenu-module_item__2DYRF","group-header":"TableMenu-module_group-header__BtvlS","col":"TableMenu-module_col__WoBci","icon-start":"TableMenu-module_icon-start__jiEUu","icon-end":"TableMenu-module_icon-end__u1Cw8","shortcut":"TableMenu-module_shortcut__3Juk6","disabled":"TableMenu-module_disabled__h5okf","focus":"TableMenu-module_focus__McuhY","active":"TableMenu-module_active__1X-uf","variant-default":"TableMenu-module_variant-default__KMuSb","secondary":"TableMenu-module_secondary__AfZLP","variant-danger":"TableMenu-module_variant-danger__mRlzi","size-default":"TableMenu-module_size-default__5QLjo","size-small":"TableMenu-module_size-small__ZfJJQ","separator":"TableMenu-module_separator__TAKK6"};
styleInject(css_248z);

const TableMenuList = /*#__PURE__*/forwardRef(function TableMenuList(props, ref) {
  const {
    children
  } = props;
  return /*#__PURE__*/jsx("div", {
    className: styles['list'],
    ref: ref,
    children: children
  });
});

function Menu(props) {
  const {
    alignment = 'bottom',
    anchor,
    children,
    className: callerClassName,
    closeOnSelect = true,
    size: size$1 = 'default',
    tag,
    onOpen,
    onClose,
    onSelect,
    UNSAFE_layout
  } = props;
  const [state, setState] = useControllableState(props.state, 'closed', state => {
    if (state === 'open') {
      onOpen === null || onOpen === void 0 || onOpen();
    } else {
      setActiveElement(null);
      onClose === null || onClose === void 0 || onClose();
    }
  });
  const {
    portalTarget
  } = useA11y();
  const {
    floatingStyles,
    refs
  } = useFloating({
    open: state === 'open',
    placement: `${alignment}-start`,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip(), size({
      apply(_ref) {
        let {
          availableHeight,
          elements
        } = _ref;
        Object.assign(elements.floating.style, {
          maxHeight: `${Math.max(50, availableHeight - 8)}px`
        });
      }
    })]
  });
  const anchorEl = anchor({
    ref: refs.setReference,
    tabIndex: 0,
    onBlur: handleAnchorBlur,
    onClick: handleAnchorClick,
    onKeyDown: handleKeyDown,
    'data-dsx--menu': tag
  });
  const listRef = useRef(null);
  const [activeElement, setActiveElement] = useState(null);
  const context = {
    activeElement,
    size: size$1,
    layout: UNSAFE_layout ?? (CSS.supports('grid-template-columns', 'subgrid') ? 'grid' : 'table'),
    onSelect: handleSelect
  };
  const className = classNames('dsx-component', styles$1['popover'], styles$1[`size-${size$1}`], callerClassName);
  function preventBlur(event) {
    // Note: this will prevent :active styling on menu items in Firefox:
    // https://bugzilla.mozilla.org/show_bug.cgi?id=771241
    event.preventDefault();
  }
  const List = {
    grid: GridMenuList,
    table: TableMenuList
  }[context.layout];
  return /*#__PURE__*/jsxs(Fragment, {
    children: [anchorEl, portalTarget && /*#__PURE__*/createPortal( /*#__PURE__*/jsx(MenuContext.Provider, {
      value: context,
      children: /*#__PURE__*/jsx(Dialog, {
        focusOnOpen: false,
        isOpen: state === 'open',
        className: className,
        ref: refs.setFloating,
        style: floatingStyles,
        onMouseDown: preventBlur,
        "data-dsx--menu--popover": tag,
        children: /*#__PURE__*/jsx(List, {
          ref: listRef,
          children: children
        })
      })
    }), portalTarget)]
  });
  function handleAnchorBlur() {
    if (state === 'open') {
      setState('closed');
    }
  }
  function handleAnchorClick() {
    setState(state === 'open' ? 'closed' : 'open');
  }
  function handleKeyDown(event) {
    const action = getActionFromKeyboardEvent(event, state === 'open');
    switch (action) {
      case 'open':
        setState('open');
        event.preventDefault();
        break;
      case 'close':
        setState('closed');
        event.preventDefault();
        break;
      case 'select':
        if (activeElement == null) {
          setState('closed');
        } else {
          activeElement.click();
        }
        event.preventDefault();
        break;
      case 'previous':
      case 'next':
      case 'first':
      case 'last':
        setActiveElement(navigateItems(action, listRef.current, activeElement));
        event.preventDefault();
        break;
    }
  }
  function handleSelect(item) {
    onSelect(item);
    closeOnSelect && setState('closed');
  }
}

function GridMenuGroupHeader(props) {
  const {
    children,
    size: callerSize,
    tag
  } = props;
  const {
    size: defaultSize
  } = useContext(MenuContext);
  const size = callerSize ?? defaultSize;
  const className = classNames(styles$2['group-header'], styles$2[`size-${size}`]);
  const content = /*#__PURE__*/jsx(Fragment, {
    children: /*#__PURE__*/jsx(Text, {
      className: styles$2['group-header-text'],
      variant: size === 'default' ? 'menu-heading' : 'menu-heading-small',
      children: children
    })
  });
  return /*#__PURE__*/jsx("div", {
    className: className,
    "data-dsx--menu-group-header": tag,
    children: content
  });
}

function TableMenuGroupHeader(props) {
  const {
    children,
    size: callerSize,
    tag
  } = props;
  const {
    size: defaultSize
  } = useContext(MenuContext);
  const size = callerSize ?? defaultSize;
  const className = classNames(styles['group-header'], styles[`size-${size}`]);
  const content = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: styles['col']
    }), /*#__PURE__*/jsx("div", {
      className: styles['col'],
      children: /*#__PURE__*/jsx(Text, {
        variant: size === 'default' ? 'menu-heading' : 'menu-heading-small',
        children: children
      })
    }), /*#__PURE__*/jsx("div", {
      className: styles['col']
    })]
  });
  return /*#__PURE__*/jsx("div", {
    className: className,
    "data-dsx--menu-group-header": tag,
    children: content
  });
}

/**
 * A menu group header for use in a menu.
 */
function MenuGroupHeader(props) {
  const {
    layout
  } = useContext(MenuContext);
  const GroupHeader = {
    grid: GridMenuGroupHeader,
    table: TableMenuGroupHeader
  }[layout];
  return /*#__PURE__*/jsx(GroupHeader, {
    ...props
  });
}

function GridMenuItem(props) {
  const {
    children,
    disabled,
    href,
    hrefType,
    icon,
    rel,
    secondary,
    selected,
    shortcut,
    size: callerSize,
    tag,
    target,
    value,
    variant = 'default'
  } = props;
  const ref = useRef(null);
  const {
    activeElement,
    size: defaultSize,
    onSelect
  } = useContext(MenuContext);
  const size = callerSize ?? defaultSize;
  const className = classNames(styles$2['item'], disabled && styles$2['disabled'], ref.current != null && activeElement === ref.current && styles$2['focus'], styles$2[`variant-${variant}`], styles$2[`size-${size}`]);
  useEffect(() => {
    if (activeElement === ref.current) {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [activeElement]);
  function handleSelect() {
    if (!disabled) {
      onSelect(value);
    }
  }
  const content = /*#__PURE__*/jsxs(Fragment, {
    children: [(selected || icon) && /*#__PURE__*/jsxs("div", {
      className: styles$2['icon-start'],
      children: [selected && /*#__PURE__*/jsx("span", {
        children: /*#__PURE__*/jsx(UNSAFE_Icon, {
          icon: MdCheck
        })
      }), icon && /*#__PURE__*/jsx("span", {
        children: /*#__PURE__*/jsx(UNSAFE_Icon, {
          icon: icon
        })
      })]
    }), /*#__PURE__*/jsx(Text, {
      className: styles$2['primary'],
      variant: size === 'default' ? 'menu-item' : 'menu-item-small',
      children: children
    }), secondary != null && /*#__PURE__*/jsx(Text, {
      className: styles$2['secondary'],
      variant: size === 'default' ? 'menu-description' : 'menu-description-small',
      children: secondary
    }), shortcut != null && /*#__PURE__*/jsx(Text, {
      className: styles$2['shortcut'],
      variant: size === 'default' ? 'menu-shortcut' : 'menu-shortcut-small',
      children: shortcut
    })]
  });
  return href ? /*#__PURE__*/jsx(Link, {
    className: className,
    href: href,
    hrefType: hrefType,
    rel: rel,
    ref: ref,
    target: target,
    onClick: handleSelect,
    "data-dsx--menu-item": tag,
    "data-dsx--menu-item--selectable": !disabled,
    children: content
  }) : /*#__PURE__*/jsx("div", {
    className: className,
    ref: ref,
    tabIndex: -1,
    onClick: handleSelect,
    "data-dsx--menu-item": tag,
    "data-dsx--menu-item--selectable": !disabled,
    children: content
  });
}

function TableMenuItem(props) {
  const {
    children,
    disabled,
    href,
    hrefType,
    icon,
    rel,
    secondary,
    selected,
    shortcut,
    size: callerSize,
    tag,
    target,
    value,
    variant = 'default'
  } = props;
  const ref = useRef(null);
  const {
    activeElement,
    size: defaultSize,
    onSelect
  } = useContext(MenuContext);
  const size = callerSize ?? defaultSize;
  const className = classNames(styles['item'], disabled && styles['disabled'], ref.current != null && activeElement === ref.current && styles['focus'], styles[`variant-${variant}`], styles[`size-${size}`]);
  useEffect(() => {
    if (activeElement === ref.current) {
      var _ref$current;
      (_ref$current = ref.current) === null || _ref$current === void 0 || _ref$current.scrollIntoView({
        block: 'nearest'
      });
    }
  }, [activeElement]);
  function handleSelect() {
    if (!disabled) {
      onSelect(value);
    }
  }
  const content = /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: styles['col'],
      children: (selected || icon) && /*#__PURE__*/jsxs("div", {
        className: styles['icon-start'],
        children: [selected && /*#__PURE__*/jsx("span", {
          children: /*#__PURE__*/jsx(UNSAFE_Icon, {
            icon: MdCheck
          })
        }), icon && /*#__PURE__*/jsx("span", {
          children: /*#__PURE__*/jsx(UNSAFE_Icon, {
            icon: icon
          })
        })]
      })
    }), /*#__PURE__*/jsxs("div", {
      className: styles['col'],
      children: [/*#__PURE__*/jsx(Text, {
        variant: size === 'default' ? 'menu-item' : 'menu-item-small',
        children: children
      }), secondary != null && /*#__PURE__*/jsx(Text, {
        className: styles['secondary'],
        variant: size === 'default' ? 'menu-description' : 'menu-description-small',
        children: secondary
      })]
    }), /*#__PURE__*/jsx("div", {
      className: styles['col'],
      children: shortcut != null && /*#__PURE__*/jsx(Text, {
        className: styles['shortcut'],
        variant: size === 'default' ? 'menu-shortcut' : 'menu-shortcut-small',
        children: shortcut
      })
    })]
  });
  return href ? /*#__PURE__*/jsx(Link, {
    className: className,
    href: href,
    hrefType: hrefType,
    rel: rel,
    ref: ref,
    target: target,
    onClick: handleSelect,
    "data-dsx--menu-item": tag,
    "data-dsx--menu-item--selectable": !disabled,
    children: content
  }) : /*#__PURE__*/jsx("div", {
    className: className,
    ref: ref,
    tabIndex: -1,
    onClick: handleSelect,
    "data-dsx--menu-item": tag,
    "data-dsx--menu-item--selectable": !disabled,
    children: content
  });
}

/**
 * A menu item for use in a menu.
 */
function MenuItem(props) {
  const {
    layout
  } = useContext(MenuContext);
  const Item = {
    grid: GridMenuItem,
    table: TableMenuItem
  }[layout];
  return /*#__PURE__*/jsx(Item, {
    ...props
  });
}

function GridMenuSeparator() {
  return /*#__PURE__*/jsx("div", {
    className: styles$2['separator']
  });
}

function TableMenuSeparator() {
  return /*#__PURE__*/jsxs("div", {
    className: styles['separator'],
    children: [/*#__PURE__*/jsx("div", {
      className: styles['col']
    }), /*#__PURE__*/jsx("div", {
      className: styles['col']
    }), /*#__PURE__*/jsx("div", {
      className: styles['col']
    }), /*#__PURE__*/jsx("div", {
      className: styles['col']
    })]
  });
}

function MenuSeparator() {
  const {
    layout
  } = useContext(MenuContext);
  const Separator = {
    grid: GridMenuSeparator,
    table: TableMenuSeparator
  }[layout];
  return /*#__PURE__*/jsx(Separator, {});
}

export { Menu, MenuGroupHeader, MenuItem, MenuSeparator };
